/* CaseTimelineTab — shared timeline view (used by both planner & doctor
 * case workspaces). Mirrors the activity log that used to live in the
 * right rail.
 *
 * Props:
 *   timeline  — case.timeline[] (already redacted by the backend for the
 *               viewer's role).
 *   viewerRole — "doctor" | "planner" (controls minor labelling).
 */
import { ClockClockwise } from "@phosphor-icons/react";

function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return "—"; }
}

const ACTION_LABEL = {
    case_submitted: "Submitted for planning",
    planner_assigned: "Routed to planner",
    planning_started: "Planning started",
    info_requested: "Asked you for more info",
    info_provided: "You provided the requested info",
    case_resubmitted_after_info: "Resubmitted after providing info",
    sent_for_approval: "Plan sent for your approval",
    submitted_to_qa: "Sent to internal QA",
    revision_requested: "Revision requested",
    case_revision_requested: "Revision requested",
    case_approved: "Plan approved",
    case_assigned_to_production: "Assigned to production",
    case_shipped: "Shipped",
    case_received: "Aligners received",
    case_refinement_requested: "Refinement requested",
    case_recalled: "Recalled to draft",
};

function actionLabel(action) {
    if (!action) return "Activity";
    if (ACTION_LABEL[action]) return ACTION_LABEL[action];
    return action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function CaseTimelineTab({ timeline, viewerRole = "doctor" }) {
    if (!timeline || timeline.length === 0) {
        return (
            <div
                className="rounded-2xl border border-dashed border-border bg-card p-12 text-center shadow-[0_1px_0_rgba(15,23,42,0.02),0_6px_18px_-14px_rgba(15,23,42,0.10)]"
                data-testid={`${viewerRole}-timeline-empty`}
            >
                <ClockClockwise size={28} className="mx-auto text-muted-foreground mb-3" />
                <div className="font-display text-lg font-bold">No activity yet</div>
                <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto">
                    Every action on this case — submissions, plan iterations, approvals, shipments — will show up here.
                </p>
            </div>
        );
    }
    return (
        <div
            className="rounded-2xl border border-border/70 bg-card overflow-hidden shadow-[0_1px_0_rgba(15,23,42,0.02),0_6px_18px_-14px_rgba(15,23,42,0.10)]"
            data-testid={`${viewerRole}-timeline-tab`}
        >
            {/* iter101.59 — Header now matches Doctor's Submission /
                Author-the-plan / MFG Spec: gray strip bg + dark caps
                title + italic subhead. Removed the violet accent bar
                and violet-tinted caps that were making Timeline feel
                separate from the rest of the tab bodies. */}
            <div className="bg-secondary/30 border-b border-border/70 px-5 sm:px-6 py-3.5">
                <h2 className="text-[11px] font-bold tracking-[0.16em] uppercase text-foreground">
                    Activity Timeline
                </h2>
                <p className="text-[12px] italic text-muted-foreground mt-0.5 max-w-xl">
                    Every event on this case in chronological order.
                </p>
            </div>
            <div className="p-5 space-y-3">
                {[...timeline].reverse().map((e) => (
                    <div
                        key={e.id}
                        className="flex gap-3 items-start text-sm border-b border-border/60 pb-3 last:border-0 last:pb-0"
                        data-testid={`${viewerRole}-timeline-entry-${e.id}`}
                    >
                        <div className="text-xs text-muted-foreground shrink-0 w-32 tabular-nums">
                            {fmtDateTime(e.created_at)}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-1.5 flex-wrap">
                                <span className="font-semibold">{e.author_name || "System"}</span>
                                <span className="text-muted-foreground">·</span>
                                <span className="text-muted-foreground text-xs">{actionLabel(e.action)}</span>
                            </div>
                            {e.note && (
                                <div className="text-foreground/85 mt-1 text-sm leading-snug whitespace-pre-wrap">{e.note}</div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
